import ActionText from '../core/ActionText';
import { v4 as uuidv4 } from 'uuid';
import Paging from '../core/Paging';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import Errored from '../core/Errored';
import Loading from '../core/Loading';
import { makeCancelable, sortObjects, isInContextComponent } from '../core/utils/utils';
import GS from '../grafsync/grafsync';
import TreeResource from './TreeResource';

const loadOptions = {};
const RESOURCE_BATCH = 100;
const PAGE_SIZE = 500;
const MAX_NODES = 5000;

class ResourceStructure extends Component {

  constructor(props) {
    super(props);
    this.state = {
      showNumber: RESOURCE_BATCH,
      page: 1,
      nodes: [],
      jumpToPageTime: null,
      hasScrolled: false,
      loadingID: undefined,
      isLoading: true,
      isLoaded: false,
      isErrored: null
    };
    this.cancellable = null;
  }

  componentDidMount() {
    this.collectData();
  }

  componentDidUpdate() {
    if (!this.state.isLoaded && this.state.loadingID !== undefined) this.collectData();
    this.timeMoreNodes();
  }

  timeMoreNodes = () => {
    if (this.moreTimeout) clearTimeout(this.moreTimeout);
    if (this.state.showNumber < this.state.nodes.length && this.state.showNumber < PAGE_SIZE) {
      this.moreTimeout = setTimeout(() => {
        this.showMore();
      }, 900);
    }
  };

  static getDerivedStateFromProps(props, state) {
    if ((props.data[props.childProperty] && props.data._version !== state.version)
      || props.viewLang !== state.viewLang
      || props.excludeDeprecated !== state.excludeDeprecated
      || (props.foundPath && props.foundTime && props.foundTime !== state.jumpToPageTime)) {
      let page = state.page;
      if (props.foundPath && props.foundTime !== state.jumpToPageTime) {
        // Make sure we do this on filtered nodes for this ontology
        const nodes = state.nodes;
        const fiActive = nodes.findIndex(item => item === props.activeResource);
        if (fiActive >= 0) {
          page = Math.floor(fiActive / PAGE_SIZE) + 1;
        }
        else {
          const fiTitle = nodes.findIndex(item => props.foundPath.includes("<" + item + ">"));
          if (fiTitle >= 0) {
            page = Math.floor(fiTitle / PAGE_SIZE) + 1;
          }
        }
      }

      // If a deletion happens that reduces page count and we're on the last page we reset page to 1
      const pageCount = Math.floor((props.data[props.childProperty].length + PAGE_SIZE - 1) / PAGE_SIZE);
      if (page > pageCount) page = 1;

      return {
        isLoading: true,
        isLoaded: false,
        loadingID: uuidv4(),
        page,
        jumpToPageTime: props.foundTime,
        viewLang: props.viewLang,
        excludeDeprecated: props.excludeDeprecated,
        ...(props.viewLang !== state.viewLang || props.excludeDeprecated !== state.excludeDeprecated) && { nodes: [] },
        version: props.data._version
      };
    }
    return null;
  }

  async collectData() {
    // We load ALL the resources necessary at this level in the tree in one API call to avoid unnecessary calls
    if (this.props.data[this.props.childProperty] && this.props.data[this.props.childProperty].length > 0) {
      if (!this.state.isLoaded && !this.state.isErrored) {
        // There may be more than 5000 children so we need to load in pages just in case!
        // We need to load everything because at this point we don't know which belong to this ontology
        const loadingID = this.state.loadingID;
        try {
          const pages = Math.floor((this.props.data[this.props.childProperty].length + MAX_NODES - 1) / MAX_NODES);
          for (let page = 0; page < pages; page++) {
            if (this.state.loadingID !== loadingID) {
              return;
            }
            const pageStart = page * MAX_NODES;
            const pageEnd = (page + 1) * MAX_NODES;
            const pageChildren = this.props.data[this.props.childProperty].slice(pageStart, pageEnd);
            await this.loadData(pageChildren).promise;
            if (this.cancellable && this.cancellable.isCancelled()) return;
          }
          // Once all loaded then we sort
          this.sortNodes();
        }
        catch (e) {
          if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ isErrored: e });
        }
      }
    }
    else {
      if ((this.cancellable && !this.cancellable.isCancelled()) || !this.cancellable)
        this.setState({ isLoading: false, isLoaded: true });
    }
  }

  sortNodes() {
    const nodes = this.props.data[this.props.childProperty].map(id => GS.data(id, this.props.graph, false));
    // We filter out resources from other ontologies in a project  
    const ontologyNodes = !this.props.filter ?
      nodes
      :
      nodes.filter(node => {
        const keep = node ?
          node.deprecated === true && this.props.excludeDeprecated ?
            false
            :
            isInContextComponent(node, this.props.ontologyIRI)
          :
          false;
        return keep;
      }
      );
    const firstLang = this.props.viewLang || this.props.defaultLang;
    const secondLang = this.props.viewLang !== this.props.defaultLang ? this.props.defaultLang : "und";
    let sortedNodes = sortObjects([...ontologyNodes], 'label', firstLang, secondLang, false).map(node => node.id);
    let page = this.state.page;
    if (this.props.foundPath) {
      const fiActive = sortedNodes.findIndex(item => item === this.props.activeResource);
      if (fiActive >= 0) {
        page = Math.floor(fiActive / PAGE_SIZE) + 1;
      }
      else {
        const fiTitle = sortedNodes.findIndex(item => this.props.foundPath.includes("<" + item + ">"));
        if (fiTitle >= 0) page = Math.floor(fiTitle / PAGE_SIZE) + 1;
      }
    }
    this.setState({ page, isLoading: false, isLoaded: true, nodes: sortedNodes, showNumber: RESOURCE_BATCH });
  }

  loadData(nodes) {
    const langs = [this.props.defaultLang];
    if (this.props.viewLang !== this.props.defaultLang) langs.push(this.props.viewLang);
    if (this.props.viewLang !== "und" && this.props.defaultLang !== "und") langs.push("und");
    const mergedLoadOptions = Object.assign({}, loadOptions, { language: langs });
    this.cancellable = makeCancelable(GS.loadData(nodes, mergedLoadOptions, this.props.graph, false, [], this.props.excludeDeprecated));
    return this.cancellable;
  }

  componentWillUnmount() {
    if (this.cancellable) this.cancellable.cancel();
    if (this.moreTimeout) clearTimeout(this.moreTimeout);
  }

  monitorNodeUpdate = (iri, property) => {
    if (property === "label" || property === "deprecated") this.sortNodes();
  };

  showMore = () => {
    const showNumber = this.state.showNumber + RESOURCE_BATCH;
    this.setState({ showNumber });
  };

  handlePageChange = async (page = 1) => this.setState({ page, showNumber: RESOURCE_BATCH });

  render() {

    const { isLoading, nodes, isErrored, showNumber, page } = this.state;
    const { onSelect, defaultLang, ontologyIRI, searchClass, excludeByPath, expandNode, iriOptions, emptyLevel,
      collapseNode, foundRef, childProperty, permissions, searchClassFilter, graph, useSlashIRI, useUuidIRI, useWorkflow,
      onAddNewNode, onDeleteNode, foundTime, foundPath, data, path, selectedTitlePath, activeResource, titlePath, onAddExistingNode, goToResource,
      expandedPaths, onBreakLink, createDialog, addTitle, isReadOnly, viewLang, titleTree } = this.props;

    const pageCount = Math.floor((nodes.length + PAGE_SIZE - 1) / PAGE_SIZE);
    const startNode = (page - 1) * PAGE_SIZE;

    if (isErrored) return <Errored data={isErrored} />;
    if (isLoading && nodes.length === 0) return <Loading offset="100px" message={this.props.data[childProperty] && this.props.data[childProperty].length > 1000 ?
      <span>This may take a few seconds.<br />There are {this.props.data[childProperty].length} resources to check.<br />They only get checked once.</span>
      :
      null} />;

    return (
      <React.Fragment>
        {pageCount > 1 &&
          <div>
            <Paging showText={false} page={page} pageCount={pageCount} handlePageChange={this.handlePageChange} />
          </div>
        }
        {nodes.length === 0 && emptyLevel &&
          <ActionText dataTest="empty-level" offset="85px">{emptyLevel}</ActionText>
        }
        {nodes.slice(startNode, startNode + showNumber).map(node => {
          // Filter paths to only send those relevant to each resource
          const expandedSubpaths = expandedPaths.filter(subPath => subPath.startsWith(path + "<" + node + ">"));
          return <TreeResource
            key={node}
            id={node}
            useWorkflow={useWorkflow}
            isReadOnly={isReadOnly}
            searchClassFilter={searchClassFilter}
            onSelect={onSelect}
            graph={graph}
            iriOptions={iriOptions}
            searchClass={searchClass}
            expandedPaths={expandedSubpaths}
            excludeByPath={excludeByPath}
            path={path + "<" + node + ">"}
            onAddExistingNode={onAddExistingNode}
            onAddNewNode={onAddNewNode}
            viewLang={viewLang}
            onDeleteNode={onDeleteNode}
            activeResource={activeResource}
            titlePath={titlePath}
            titleTree={titleTree}
            goToResource={goToResource}
            selectedTitlePath={selectedTitlePath}
            expandNode={expandNode}
            childProperty={childProperty}
            collapseNode={collapseNode}
            onBreakLink={onBreakLink}
            parentIRI={data.id}
            addTitle={addTitle}
            foundRef={foundRef}
            foundTime={foundTime}
            ontologyIRI={ontologyIRI}
            useSlashIRI={useSlashIRI}
            useUuidIRI={useUuidIRI}
            foundPath={foundPath}
            defaultLang={defaultLang}
            createDialog={createDialog}
            permissions={permissions}
            loadOptions={{
              language: viewLang && viewLang !== defaultLang ?
                viewLang === "und" || defaultLang === "und" ?
                  [defaultLang, viewLang]
                  :
                  [defaultLang, viewLang, "und"]
                :
                defaultLang === "und" ?
                  [defaultLang]
                  :
                  [defaultLang, "und"]
            }}
            updateEvent={this.monitorNodeUpdate}
          />;
        }
        )}
        {pageCount > 1 &&
          <div>
            <Paging showText={false} page={page} pageCount={pageCount} handlePageChange={this.handlePageChange} />
          </div>
        }
      </React.Fragment>
    );
  }

}

ResourceStructure.propTypes = {
  filter: PropTypes.bool,
  searchClassFilter: PropTypes.array.isRequired,
  ontologyIRI: PropTypes.string.isRequired,
  useSlashIRI: PropTypes.bool,
  useUuidIRI: PropTypes.bool,
  defaultLang: PropTypes.string.isRequired,
  path: PropTypes.string.isRequired,
  searchClass: PropTypes.string,
  excludeByPath: PropTypes.func,
  goToResource: PropTypes.func,
  expandedPaths: PropTypes.arrayOf(PropTypes.string).isRequired,
  expandNode: PropTypes.func.isRequired,
  collapseNode: PropTypes.func.isRequired,
  onAddExistingNode: PropTypes.func.isRequired,
  onAddNewNode: PropTypes.func,
  onDeleteNode: PropTypes.func.isRequired,
  childProperty: PropTypes.string.isRequired,
  foundTime: PropTypes.number,
  excludeDeprecated: PropTypes.bool,
  titlePath: PropTypes.array.isRequired,
  selectedTitlePath: PropTypes.array,
  version: PropTypes.number,
  useWorkflow: PropTypes.bool,
  activeResource: PropTypes.string,
  foundPath: PropTypes.string,
  isReadOnly: PropTypes.bool,
  addTitle: PropTypes.string,
  foundRef: PropTypes.object,
  viewLang: PropTypes.string,
  graph: PropTypes.string.isRequired,
  titleTree: PropTypes.array,
  data: PropTypes.object.isRequired,
  onSelect: PropTypes.func.isRequired,
  permissions: PropTypes.array.isRequired,
  createDialog: PropTypes.object,
  iriOptions: PropTypes.object,
  emptyLevel: PropTypes.string
};

export default ResourceStructure;